import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { X, Info, Zap } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Per-report scheduling + cache TTL editor.
 *
 * Splits the traditional "single-cron-for-everything" model into TWO
 * independent schedules: a fast/cheap Stories schedule (daily-ish) and a
 * slower/expensive Posts schedule (weekly-ish). Both are OFF by default —
 * nothing auto-fires until the user explicitly toggles Auto-track ON and
 * picks cadences.
 *
 * Also exposes cache-TTL fields so the user (never the code) decides how
 * fresh data has to be before a re-fetch is triggered.
 */

// Common cron presets — user can still edit the raw cron string below.
const STORIES_PRESETS = [
  { label: "Off (manual only)",     value: "" },
  { label: "Every day, 10:00 IST",  value: "0 10 * * *" },
  { label: "Every day, 08:00 IST",  value: "0 8 * * *" },
  { label: "Every 6 hours",         value: "0 */6 * * *" },
];

const POSTS_PRESETS = [
  { label: "Off (manual only)",          value: "" },
  { label: "Every day, 10:00 IST",       value: "0 10 * * *" },
  { label: "Every Thursday, 10:00 IST",  value: "0 10 * * 4" },
  { label: "Every Monday, 10:00 IST",    value: "0 10 * * 1" },
  { label: "Every Sunday, 10:00 IST",    value: "0 10 * * 0" },
  { label: "1st of the month, 10:00 IST","value": "0 10 1 * *" },
  { label: "15th of the month, 10:00 IST","value": "0 10 15 * *" },
];

export function ScheduleModal({ open, onClose, report, onSaved }) {
  const [autoTrack, setAutoTrack] = useState(false);
  const [storiesCron, setStoriesCron] = useState("");
  const [postsCron, setPostsCron] = useState("");
  const [postsLookback, setPostsLookback] = useState(24);
  const [ttlProfile, setTtlProfile] = useState("");
  const [ttlLive, setTtlLive] = useState("");
  const [ttlHighlights, setTtlHighlights] = useState("");
  const [ttlPosts, setTtlPosts] = useState("");
  const [saving, setSaving] = useState(false);

  // Init form state exactly once per dialog opening — the 8s reports poll
  // swaps the `report` object identity, and depending on it here wiped any
  // in-progress edits mid-typing.
  const initRef = useRef(false);
  useEffect(() => {
    if (!open) { initRef.current = false; return; }
    if (!report || initRef.current) return;
    initRef.current = true;
    setAutoTrack(!!report.auto_track);
    setStoriesCron(report.stories_cron || "");
    setPostsCron(report.posts_cron || "");
    setPostsLookback(report.posts_lookback_limit ?? 24);
    setTtlProfile(report.cache_ttl_profile_minutes ?? "");
    setTtlLive(report.cache_ttl_stories_live_minutes ?? "");
    setTtlHighlights(report.cache_ttl_highlights_minutes ?? "");
    setTtlPosts(report.cache_ttl_posts_minutes ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!report) return null;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        auto_track: autoTrack,
        stories_cron: storiesCron.trim() || null,
        posts_cron: postsCron.trim() || null,
        posts_lookback_limit: postsLookback ? Number(postsLookback) : null,
        cache_ttl_profile_minutes: ttlProfile === "" ? null : Number(ttlProfile),
        cache_ttl_stories_live_minutes: ttlLive === "" ? null : Number(ttlLive),
        cache_ttl_highlights_minutes: ttlHighlights === "" ? null : Number(ttlHighlights),
        cache_ttl_posts_minutes: ttlPosts === "" ? null : Number(ttlPosts),
      };
      await axios.put(`${API}/reports/${report.id}`, payload);
      toast.success("Schedule updated");
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save schedule");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-2xl bg-white border-slate-200 text-slate-900"
        data-testid="schedule-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-slate-900 flex items-center gap-2">
            <Zap size={16} className="text-emerald-400" />
            Schedule &amp; Refresh — {report.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* Master switch */}
          <div className="flex items-center justify-between p-3 rounded-md bg-slate-50 border border-slate-200">
            <div>
              <div className="text-sm font-medium text-slate-900">Auto-track</div>
              <div className="text-xs text-slate-400">
                Master switch. When OFF, neither Stories nor Posts auto-refresh.
              </div>
            </div>
            <Switch
              checked={autoTrack}
              onCheckedChange={setAutoTrack}
              data-testid="schedule-auto-track"
            />
          </div>

          {/* Stories cadence */}
          <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
            <Label className="text-xs uppercase text-slate-500 tracking-wider">
              Stories cadence
            </Label>
            <select
              className="mt-2 w-full bg-white border border-slate-200 rounded-md h-9 px-2 text-sm text-slate-900"
              value={STORIES_PRESETS.find(p => p.value === storiesCron)?.value ?? ""}
              onChange={(e) => setStoriesCron(e.target.value)}
              data-testid="schedule-stories-preset"
            >
              {STORIES_PRESETS.map((p) => (
                <option key={p.label} value={p.value}>{p.label}</option>
              ))}
              {storiesCron && !STORIES_PRESETS.find(p => p.value === storiesCron) && (
                <option value={storiesCron}>{storiesCron} (custom)</option>
              )}
            </select>
            <Input
              className="mt-2 bg-white border-slate-200 text-slate-900 h-8 text-xs font-mono"
              placeholder="Custom cron (5 fields) — e.g. 0 10 * * *"
              value={storiesCron}
              onChange={(e) => setStoriesCron(e.target.value)}
              data-testid="schedule-stories-cron"
            />
            <div className="text-[11px] text-slate-400 mt-1">
              Fetches profile followers + live-24h stories + highlights (if enabled).
              ~2 RapidAPI calls per handle per fire.
            </div>
          </div>

          {/* Posts cadence */}
          <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
            <Label className="text-xs uppercase text-slate-500 tracking-wider">
              Posts cadence
            </Label>
            <select
              className="mt-2 w-full bg-white border border-slate-200 rounded-md h-9 px-2 text-sm text-slate-900"
              value={POSTS_PRESETS.find(p => p.value === postsCron)?.value ?? ""}
              onChange={(e) => setPostsCron(e.target.value)}
              data-testid="schedule-posts-preset"
            >
              {POSTS_PRESETS.map((p) => (
                <option key={p.label} value={p.value}>{p.label}</option>
              ))}
              {postsCron && !POSTS_PRESETS.find(p => p.value === postsCron) && (
                <option value={postsCron}>{postsCron} (custom)</option>
              )}
            </select>
            <Input
              className="mt-2 bg-white border-slate-200 text-slate-900 h-8 text-xs font-mono"
              placeholder="Custom cron (5 fields) — e.g. 0 10 * * 4"
              value={postsCron}
              onChange={(e) => setPostsCron(e.target.value)}
              data-testid="schedule-posts-cron"
            />
            <div className="mt-2 flex items-center gap-2">
              <Label className="text-xs text-slate-500">Lookback (last N posts):</Label>
              <Input
                type="number"
                min="1"
                max="500"
                className="w-24 bg-white border-slate-200 text-slate-900 h-7 text-xs"
                value={postsLookback}
                onChange={(e) => setPostsLookback(e.target.value)}
                data-testid="schedule-posts-lookback"
              />
            </div>
            <div className="text-[11px] text-slate-400 mt-1">
              How many recent posts per handle to scan on each fire.
            </div>
          </div>

          {/* Cache TTLs (advanced) */}
          <details className="p-3 rounded-md bg-slate-50 border border-slate-200">
            <summary className="text-xs uppercase text-slate-500 tracking-wider cursor-pointer">
              Cache TTL — how fresh must data be? <Info size={11} className="inline ml-1" />
            </summary>
            <div className="grid grid-cols-2 gap-3 mt-3">
              {[
                ["Profile (followers)", ttlProfile, setTtlProfile, "1440", "cache-ttl-profile"],
                ["Live stories",        ttlLive,    setTtlLive,    "30",   "cache-ttl-live"],
                ["Highlights",          ttlHighlights, setTtlHighlights, "1440", "cache-ttl-highlights"],
                ["Posts feed",          ttlPosts,   setTtlPosts,   "360",  "cache-ttl-posts"],
              ].map(([label, val, setVal, ph, tid]) => (
                <div key={label}>
                  <Label className="text-[11px] text-slate-500">{label} (minutes)</Label>
                  <Input
                    type="number"
                    min="0"
                    className="bg-white border-slate-200 text-slate-900 h-8 text-xs"
                    placeholder={`default: ${ph}`}
                    value={val}
                    onChange={(e) => setVal(e.target.value)}
                    data-testid={tid}
                  />
                </div>
              ))}
            </div>
            <div className="text-[11px] text-slate-400 mt-2">
              Data younger than the TTL is served from a shared handle-cache, so
              two reports touching the same handle only cost credits ONCE. Leave
              blank to use system defaults. Set to <b>0</b> to always re-fetch
              (no cache).
            </div>
          </details>
        </div>

        <DialogFooter className="mt-2">
          <Button
            variant="outline"
            className="bg-white border-slate-200 text-slate-700"
            onClick={onClose}
            data-testid="schedule-cancel"
          >
            <X size={13} className="mr-1.5" />Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-emerald-500 hover:bg-emerald-600 text-black"
            data-testid="schedule-save"
          >
            {saving ? "Saving..." : "Save schedule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
